import MojitoCore from 'mojito/core';
import { STORE_KEY, initialState } from './slice.js';
import BonusTypes from 'services/bonus/types.js';
import ServiceTypes from 'services/common/types.js';

const reduxInstance = MojitoCore.Services.redux;
const { BONUS_FILTER } = BonusTypes;
const { CONTENT_STATE } = ServiceTypes;
const EMPTY_ARRAY = [];

/**
 * Bonus campaign selectors.
 *
 * @module BonusCampaignSelectors
 * @name selectors
 * @memberof Mojito.Services.BonusCampaign
 */

/**
 * Selects bonus campaigns state.
 *
 * @function selectState
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Mojito.Services.BonusCampaign} The state of the bonus campaigns store.
 * @memberof Mojito.Services.BonusCampaign.selectors
 */
export const selectState = state => {
    // Should be no need to fallback to global state once fully migrated to Redux.
    const appState = state || reduxInstance.store?.getState();
    return appState?.[STORE_KEY] || initialState;
};

/**
 * Selects campaigns loading state.
 *
 * @function selectCampaignLoadingState
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Mojito.Services.Common.types.CONTENT_STATE} Loading state.
 *
 * @memberof Mojito.Services.BonusCampaign.selectors
 */
export const selectCampaignLoadingState = state => selectState(state).loadingState;

/**
 * Selects if campaigns have pending loading state.
 *
 * @function selectCampaignsPending
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {boolean} True if fetching campaigns.
 *
 * @memberof Mojito.Services.BonusCampaign.selectors
 */
export const selectCampaignsPending = state =>
    selectCampaignLoadingState(state) === CONTENT_STATE.PENDING;

/**
 * Selects active bonus campaigns state.
 *
 * @function selectActiveBonusCampaigns
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Array<Mojito.Services.Bonus.type.Campaign>} Active bonus campaigns.
 * @memberof Mojito.Services.BonusCampaign.selectors
 */
export const selectActiveBonusCampaigns = state =>
    selectState(state).campaignsByFilter[BONUS_FILTER.ACTIVE] || EMPTY_ARRAY;

/**
 * Selects in progress bonus campaigns state.
 *
 * @function selectInprogressBonusCampaigns
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Array<Mojito.Services.Bonus.type.InProgressBonus>} In progress bonus campaigns.
 * @memberof Mojito.Services.BonusCampaign.selectors
 */
export const selectInprogressBonusCampaigns = state =>
    selectState(state).campaignsByFilter[BONUS_FILTER.IN_PROGRESS] || EMPTY_ARRAY;

/**
 * Selects historic bonus campaigns state.
 *
 * @function selectHistoricBonusCampaigns
 *
 * @param {object} [state] - Redux state object. The application state from {@link Mojito.Core.Services.redux#getStore|global store} will be used if not provided.
 * @returns {Array<Mojito.Services.Bonus.type.Campaign>} Historic bonus campaigns.
 * @memberof Mojito.Services.BonusCampaign.selectors
 */
export const selectHistoricBonusCampaigns = state =>
    selectState(state).campaignsByFilter[BONUS_FILTER.HISTORIC] || EMPTY_ARRAY;
